'use client';

import { usePublicAnalytics } from '@/lib/analytics/use-public-analytics';

import { Suspense, useEffect, useState } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';
import {
  COOKIE_CONSENT_CHANGED_EVENT,
  GOOGLE_ANALYTICS_READY_EVENT,
  isAnalyticsCookieConsentAccepted,
  isPostHogEnabledForPathname,
} from '@/lib/analytics/posthog-paths';

const GA_MEASUREMENT_ID = 'G-NQYFQGXRZ8';

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function PageView() {
  const publicAnalytics = usePublicAnalytics();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const [consentOk, setConsentOk] = useState(false);
  const [analyticsReady, setAnalyticsReady] = useState(false);

  useEffect(() => {
    const readConsent = () => setConsentOk(isAnalyticsCookieConsentAccepted());
    const markAnalyticsReady = () =>
      setAnalyticsReady(typeof window.gtag === 'function');

    readConsent();
    markAnalyticsReady();
    window.addEventListener(COOKIE_CONSENT_CHANGED_EVENT, readConsent);
    window.addEventListener(GOOGLE_ANALYTICS_READY_EVENT, markAnalyticsReady);
    return () => {
      window.removeEventListener(COOKIE_CONSENT_CHANGED_EVENT, readConsent);
      window.removeEventListener(
        GOOGLE_ANALYTICS_READY_EVENT,
        markAnalyticsReady,
      );
    };
  }, []);

  useEffect(() => {
    if (
      !publicAnalytics ||
      !consentOk ||
      !analyticsReady ||
      typeof window.gtag !== 'function'
    ) {
      return;
    }
    // Private app areas are excluded from all analytics.
    if (!isPostHogEnabledForPathname(pathname)) {
      return;
    }
    const search = searchParams?.toString();
    const page_path = search ? `${pathname}?${search}` : pathname;
    window.gtag('event', 'page_view', {
      send_to: GA_MEASUREMENT_ID,
      page_path,
      page_location: window.location.href,
      page_title: document.title,
    });
  }, [pathname, searchParams, consentOk, analyticsReady, publicAnalytics]);

  return null;
}

export function GoogleAnalyticsPageView() {
  return (
    <Suspense fallback={null}>
      <PageView />
    </Suspense>
  );
}
